import { useState } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { images } from '../assets/images';
import { API_URL } from '../config';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    interest: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/public/enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        alert('Enquiry sent successfully! We will contact you soon.');
        setFormData({ name: '', phone: '', email: '', interest: '', message: '' });
      } else {
        alert('Failed to send enquiry. Please try again.');
      }
    } catch (err) {
      console.error(err);
      alert('Failed to send enquiry. Please check your connection.');
    }
  };

  return (
    <div className="pt-16 pb-24">
      <div className="bg-slate-900 text-white py-20 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
        <p className="text-slate-400 max-w-2xl mx-auto px-4">Get in touch with our team for enquiries and support.</p>
      </div>
      <div className="container mx-auto px-4 mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white p-8 rounded-lg shadow-lg border border-slate-100">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Send us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Your Name</label>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors" 
                    placeholder="John Doe" 
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors" 
                    placeholder="+1 (555) 000-0000" 
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors" 
                  placeholder="john@example.com" 
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Product Requirement</label>
                <select 
                  name="interest"
                  value={formData.interest}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors"
                >
                  <option value="">Select a product category</option>
                  <option value="Industrial Capacitors">Industrial Capacitors</option>
                  <option value="Fan Regulators">Fan Regulators</option>
                  <option value="Motors">Motors</option>
                  <option value="Lighting">Lighting</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                <textarea 
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4} 
                  className="w-full px-4 py-3 rounded border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors" 
                  placeholder="Tell us about your requirements..."
                  required
                ></textarea>
              </div>
              <button type="submit" className="w-full bg-orange-600 text-white py-4 rounded font-bold hover:bg-orange-700 transition-colors uppercase tracking-wide">
                Send Enquiry
              </button>
            </form>
          </div>

          {/* Contact Info & Map */}
          <div className="space-y-8">
            <div className="bg-slate-50 p-8 rounded-lg border-l-4 border-slate-900">
              <h3 className="text-xl font-bold text-slate-900 mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-white p-3 rounded-full shadow-sm text-orange-600">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">Factory & Office</p>
                    <p className="text-slate-600">54/C-7, Rama Road Industrial Area, Near Najafgarh Road Indl. Area, West Delhi, Delhi, 110015<br /></p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-white p-3 rounded-full shadow-sm text-orange-600">
                    <Phone size={24} />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">Phone</p>
                    <p className="text-slate-600">+91 797 330 9949</p>
                    <p className="text-slate-600"></p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-white p-3 rounded-full shadow-sm text-orange-600">
                    <Mail size={24} />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">Email</p>
                    <p className="text-slate-600">support@carlosonestop.com</p>
                    <p className="text-slate-600"></p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-white p-3 rounded-full shadow-sm text-orange-600">
                    <Clock size={24} />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">Working Hours</p>
                    <p className="text-slate-600">Mon - Sat: 9:00 AM - 6:00 PM</p>
                    <p className="text-slate-600">Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="bg-slate-200 h-64 rounded-lg overflow-hidden relative">
              <div className="absolute inset-0 flex items-center justify-center text-slate-500 font-medium">
                Google Map Integration
              </div>
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3501.366551693636!2d77.1439!3d28.6517!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDM5JzA2LjEiTiA3N8KwMDgnMzguMCJF!5e0!3m2!1sen!2sin!4v1635000000000!5m2!1sen!2sin" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen 
                loading="lazy"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
